import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, DollarSign, TrendingUp, RefreshCw } from 'lucide-react';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import toast from 'react-hot-toast';

import { dashboardAPI } from '../api/dashboard';
import { DashboardData, DashboardDateRange } from '../types/dashboard';
import {
  DashboardStats,
  RevenueChart,
  VehicleComparisonChart,
  TripQuickView,
  VehicleQuickView,
  AlertPanel,
  PendingReceiptWidget,
  StockAlertWidget,
} from '../components/Dashboard';
import { DateRangeQuickFilter } from '../components/UI/DateRangeQuickFilter';
import { useOnboarding } from '../hooks/useOnboarding';

const getDateRange = (range: DashboardDateRange): { start: Date; end: Date } => {
  const now = new Date();
  switch (range) {
    case 'today':
      return { start: startOfDay(now), end: endOfDay(now) };
    case 'thisWeek':
      return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
    case 'thisMonth':
      return { start: startOfMonth(now), end: endOfMonth(now) };
    default:
      return { start: startOfDay(now), end: endOfDay(now) };
  }
};

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { shouldShowTour } = useOnboarding();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData | null>(null);
  const [dateRange, setDateRange] = useState<DashboardDateRange>('thisMonth');
  const [startDate, setStartDate] = useState(() => format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(() => format(endOfMonth(new Date()), 'yyyy-MM-dd'));

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dashboardAPI.getData({
        startDate,
        endDate,
        includeAlerts: true,
      });
      setData(result);
    } catch (error: any) {
      console.error('Failed to load dashboard:', error);
      toast.error(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, t]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleQuickRange = (range: DashboardDateRange) => {
    setDateRange(range);
    const { start, end } = getDateRange(range);
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
  };

  const handleDateChange = (start: string, end: string) => {
    setDateRange('custom');
    setStartDate(start);
    setEndDate(end);
  };

  const quickLinks = [
    { path: '/trips/new', labelKey: 'dashboard.createTrip', icon: FileText, color: 'bg-blue-500' },
    { path: '/expenses/new', labelKey: 'dashboard.recordExpense', icon: DollarSign, color: 'bg-green-500' },
    { path: '/reports', labelKey: 'dashboard.viewReports', icon: TrendingUp, color: 'bg-purple-500' },
  ];

  // Default empty data for loading state
  const emptyData: DashboardData = {
    summary: {
      totalRevenue: 0,
      totalExpenses: 0,
      totalProfit: 0,
      profitMargin: 0,
      activeTripsCount: 0,
      completedTripsCount: 0,
      activeVehiclesCount: 0,
      idleVehiclesCount: 0,
      totalOutstandingDebt: 0,
      supplierDebt: 0,
      customerDebt: 0,
    },
    revenueChart: [],
    vehicleChart: [],
    recentTrips: [],
    topVehicles: [],
    alerts: [],
    dateRange: { startDate, endDate },
  };

  const displayData = data || emptyData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">{t('dashboard.title')}</h1>
        
        <div className="flex items-center gap-4">
          {/* Quick Range Buttons */}
          <div className="flex gap-2">
            {(['today', 'thisWeek', 'thisMonth'] as DashboardDateRange[]).map((range) => (
              <button
                key={range}
                onClick={() => handleQuickRange(range)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === range
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t(`dateFilter.${range}`)}
              </button>
            ))}
          </div>

          {/* Refresh Button */}
          <button
            onClick={loadDashboard}
            disabled={loading}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('common.refresh')}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="card p-4">
        <DateRangeQuickFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={(date) => handleDateChange(date, endDate)}
          onEndDateChange={(date) => handleDateChange(startDate, date)}
        />
      </div>

      {/* Alerts Panel */}
      {displayData.alerts.length > 0 && (
        <AlertPanel alerts={displayData.alerts} />
      )}

      {/* Phase 2: Pending Receipt Widget */}
      <PendingReceiptWidget />

      {/* Phase 6: Stock Alert Widget */}
      <StockAlertWidget />

      {/* Summary Stats */}
      <DashboardStats summary={displayData.summary} loading={loading} />

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RevenueChart data={displayData.revenueChart} loading={loading} />
        <VehicleComparisonChart data={displayData.vehicleChart} loading={loading} />
      </div>

      {/* Quick View Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TripQuickView trips={displayData.recentTrips} loading={loading} />
        <VehicleQuickView vehicles={displayData.topVehicles} loading={loading} />
      </div>

      {/* Quick Actions */}
      <div className="card p-4">
        <h2 className="text-lg font-semibold mb-4">{t('dashboard.quickActions')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {quickLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className={`${link.color} p-2 rounded-lg`}>
                <link.icon className="w-5 h-5 text-white" />
              </div>
              <span className="ml-3 font-medium text-gray-700">{t(link.labelKey)}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};
